import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SEOHead } from '../../components/seo/SEOHead';
import { Input, Label, Textarea } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../lib/api';
import { CONSULTATION_FEE_INR, CONSULTATION_DURATION_MINUTES } from '@bharat-consultancy/shared';

/**
 * Standalone booking form for the "talk before you pay" consultation
 * call. Deliberately not gated behind login (contrast with
 * Booking/BookingForm.tsx) — the whole point of this flow is letting a
 * not-yet-convinced visitor pay without first creating an account.
 */
export default function ConsultBookingForm() {
  const [searchParams] = useSearchParams();
  const serviceId = searchParams.get('serviceId') ?? undefined;
  const serviceName = searchParams.get('serviceName') ?? undefined;
  const navigate = useNavigate();
  const { user } = useAuth();

  const [form, setForm] = useState({
    name: '',
    email: '',
    mobile: '',
    whatsapp: '',
    preferredDate: '',
    preferredTime: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        name: user.name ?? f.name,
        email: user.email ?? f.email,
      }));
    }
  }, [user]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      const res = await api.consultations.create({
        serviceId,
        name: form.name,
        email: form.email,
        mobile: form.mobile,
        whatsapp: form.whatsapp || undefined,
        preferredDate: form.preferredDate || undefined,
        preferredTime: form.preferredTime || undefined,
        message: form.message || undefined,
      });
      navigate(`/consult/summary?bookingId=${res.booking.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create your booking. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <SEOHead
        title="Book a Consultation — Bharat Consultancy"
        description={`Book a ${CONSULTATION_DURATION_MINUTES}-minute consultation call with Bharat Consultancy for ₹${CONSULTATION_FEE_INR}.`}
        canonicalPath="/consult"
      />

      <section className="bg-paper py-10">
        <div className="mx-auto max-w-2xl px-4 sm:px-6">
          <div className="rounded-lg border border-line bg-white p-6">
            <p className="font-mono text-xs uppercase tracking-widest text-seal-blue">
              {CONSULTATION_DURATION_MINUTES}-Minute Consultation
            </p>
            <h1 className="mt-2 font-display text-2xl font-bold text-ink">
              {serviceName ? `Talk to Us About ${serviceName}` : 'Talk to a Consultant Before You Pay'}
            </h1>
            <p className="mt-2 text-sm text-ink/60">
              Pay ₹{CONSULTATION_FEE_INR} to book a {CONSULTATION_DURATION_MINUTES}-minute call. Our consultant will
              discuss your requirements and share a clear quotation — no obligation to proceed with any service.
            </p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="mobile">Mobile Number</Label>
                  <Input
                    id="mobile"
                    type="tel"
                    required
                    value={form.mobile}
                    onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="whatsapp">WhatsApp Number (optional)</Label>
                  <Input
                    id="whatsapp"
                    type="tel"
                    value={form.whatsapp}
                    onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="preferredDate">Preferred Call Date</Label>
                  <Input
                    id="preferredDate"
                    type="date"
                    value={form.preferredDate}
                    onChange={(e) => setForm({ ...form, preferredDate: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="preferredTime">Preferred Call Time</Label>
                  <Input
                    id="preferredTime"
                    type="time"
                    value={form.preferredTime}
                    onChange={(e) => setForm({ ...form, preferredTime: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="message">What would you like to discuss?</Label>
                <Textarea
                  id="message"
                  rows={3}
                  placeholder="Briefly describe your requirement so our consultant can prepare"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                />
              </div>

              {error && <p className="text-sm text-red-600">{error}</p>}

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : `Continue — Pay ₹${CONSULTATION_FEE_INR}`}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
